import { Typography } from 'antd';
import { CheckCircleFilled, HistoryOutlined } from '@ant-design/icons';
import type { DesignerDiagnostic } from './diagOverlay';

const { Text } = Typography;

const SEV_SYMBOL: Record<DesignerDiagnostic['severity'], string> = {
  error: '●',
  warning: '▲',
  info: 'ℹ',
};
const SEV_VAR: Record<DesignerDiagnostic['severity'], string> = {
  error: 'var(--ol-negative)',
  warning: 'var(--ol-attention)',
  info: 'var(--ol-info)',
};

/**
 * The problems drawer (spec §7.2 / §8). Lists compiler diagnostics; each row pans-and-highlights its
 * element (click-to-focus). Diagnostics carry element refs (nodeId/edgeId) from the server analyser.
 * Rows marked `stale` are findings from the LAST validation — the flow changed since (the graph
 * edit flipped `serverValidated` off); they read as history, never as the current verdict.
 */
export function ProblemsPanel({
  diagnostics,
  serverValidated,
  onFocus,
}: {
  diagnostics: DesignerDiagnostic[];
  serverValidated: boolean;
  onFocus: (d: DesignerDiagnostic) => void;
}) {
  if (diagnostics.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ color: 'var(--ol-positive)', fontSize: 22, marginBottom: 6 }}>
          <CheckCircleFilled />
        </div>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          {serverValidated
            ? 'No compiler findings — this definition compiles.'
            : 'No preview findings. Run Validate for the full compiler pass.'}
        </Text>
      </div>
    );
  }

  const hasStale = diagnostics.some((d) => d.stale);

  return (
    <div style={{ overflow: 'auto', height: '100%', padding: '6px 8px' }}>
      {hasStale && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 6px 6px',
            color: 'var(--ol-text-tertiary)',
            fontSize: 11,
          }}
        >
          <HistoryOutlined aria-hidden />
          <span>
            Findings tagged <b>last validation</b> describe the flow as it was when Validate last
            ran — it changed since. Re-run Validate for the current verdict.
          </span>
        </div>
      )}
      {diagnostics.map((d, i) => (
        <div
          key={i}
          className={`ol-problem${d.stale ? ' ol-problem--stale' : ''}`}
          role="button"
          tabIndex={0}
          aria-label={`${d.severity} ${d.code}: ${d.message}${d.stale ? ' (from last validation)' : ''}`}
          onClick={() => onFocus(d)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onFocus(d);
          }}
        >
          <span style={{ color: SEV_VAR[d.severity], flex: 'none' }}>{SEV_SYMBOL[d.severity]}</span>
          <span style={{ minWidth: 0 }}>
            <span className="ol-problem__code">{d.code}</span>
            {d.nodeId && <span className="ol-problem__code"> · {d.nodeId}</span>}
            {d.edgeId && <span className="ol-problem__code"> · edge</span>}{' '}
            {d.message}
            {d.source === 'preview' && (
              <Text type="secondary" style={{ fontSize: 10 }}>
                {' '}
                (preview)
              </Text>
            )}
          </span>
          {d.stale && <span className="ol-problem__stale-tag">last validation</span>}
        </div>
      ))}
    </div>
  );
}
